/**
 * ============================================================================
 * [P2-01] ScriptDetailPanel - 스크립트 상세 패널 컴포넌트
 *
 * 기능:
 * - 읽기 모드: 스크립트 상세 내용 표시 (헤더, 메타, 내용, 액션)
 * - 수정 모드: 이름/카테고리/내용 편집
 * - 모드 전환: read ↔ edit, 미저장 변경사항 confirm
 * - 액션: 수정, 삭제, 복사, 닫기(unpin)
 *
 * [DP-01] 듀얼 패널 고정 기능
 * [2026-03-02] 신규 생성
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useCallback, useEffect } from 'react'
import { useScripts } from '@/hooks'
import { CharacterCounter } from './CharacterCounter'
import { CategoryFilter } from './CategoryFilter'
import type { Script, PanelIndex, DetailPanelMode } from '@/types'
import styles from './DualPanelView.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface ScriptDetailPanelProps {
  script: Script
  panelIndex: PanelIndex           // 0=패널1, 1=패널2
  mode: DetailPanelMode            // 'read' | 'edit'
  onModeChange: (mode: DetailPanelMode) => void
  onClose: () => void              // 패널 닫기 (unpin)
  onDelete: (id: string) => void   // 스크립트 삭제
  onOpenInEditor?: (script: Script) => void  // 전체 에디터에서 열기
  mobileHidden?: boolean           // 모바일에서 숨김 여부
}

// ============================================================================
// 3. ScriptDetailPanel Component
// ============================================================================
export function ScriptDetailPanel({
  script,
  panelIndex,
  mode,
  onModeChange,
  onClose,
  onDelete,
  onOpenInEditor,
  mobileHidden = false,
}: ScriptDetailPanelProps) {
  // ========================================================================
  // 3-1. Hooks
  // ========================================================================
  const { editScript, categories } = useScripts()

  // ========================================================================
  // 3-2. 수정 모드 로컬 상태
  // ========================================================================
  const [editName, setEditName] = useState(script.name)
  const [editCategory, setEditCategory] = useState(script.category ?? '')
  const [editContent, setEditContent] = useState(script.content)
  const [isSaving, setIsSaving] = useState(false)

  // ========================================================================
  // 3-3. 스크립트 변경 시 로컬 상태 동기화
  // ========================================================================
  useEffect(() => {
    setEditName(script.name)
    setEditCategory(script.category ?? '')
    setEditContent(script.content)
  }, [script.id, script.name, script.category, script.content])

  // ========================================================================
  // 3-4. 미저장 변경사항 확인
  // ========================================================================
  const hasUnsavedChanges = useCallback(() => {
    return (
      editName !== script.name ||
      editCategory !== (script.category ?? '') ||
      editContent !== script.content
    )
  }, [editName, editCategory, editContent, script.name, script.category, script.content])

  // ========================================================================
  // 3-5. 모드 전환 핸들러
  // ========================================================================
  const handleEnterEdit = useCallback(() => {
    onModeChange('edit')
  }, [onModeChange])

  const handleCancelEdit = useCallback(() => {
    if (hasUnsavedChanges()) {
      if (!window.confirm('수정 중인 내용이 있습니다. 취소하시겠습니까?')) {
        return
      }
    }
    // 원래 값으로 복원
    setEditName(script.name)
    setEditCategory(script.category ?? '')
    setEditContent(script.content)
    onModeChange('read')
  }, [hasUnsavedChanges, script, onModeChange])

  // ========================================================================
  // 3-6. 저장 핸들러
  // ========================================================================
  const handleSave = useCallback(async () => {
    if (!editName.trim()) {
      alert('스크립트 이름을 입력하세요.')
      return
    }

    setIsSaving(true)
    try {
      const success = await editScript(script.id, {
        name: editName.trim(),
        category: editCategory.trim() || undefined,
        content: editContent,
      })

      if (success) {
        onModeChange('read')
      } else {
        alert('저장에 실패했습니다.')
      }
    } finally {
      setIsSaving(false)
    }
  }, [editScript, script.id, editName, editCategory, editContent, onModeChange])

  // ========================================================================
  // 3-7. 복사 핸들러
  // ========================================================================
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(script.content)
      alert('클립보드에 복사되었습니다.')
    } catch {
      // Fallback
      const textArea = document.createElement('textarea')
      textArea.value = script.content
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      alert('클립보드에 복사되었습니다.')
    }
  }, [script.content])

  // ========================================================================
  // 3-8. 닫기(unpin) 핸들러
  // ========================================================================
  const handleClose = useCallback(() => {
    if (mode === 'edit' && hasUnsavedChanges()) {
      if (!window.confirm('수정 중인 내용이 있습니다. 닫으시겠습니까?')) {
        return
      }
    }
    onClose()
  }, [mode, hasUnsavedChanges, onClose])

  // ========================================================================
  // 3-9. 날짜 포맷
  // ========================================================================
  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // ========================================================================
  // 3-10. CSS 클래스
  // ========================================================================
  const panelClassName = `${styles.detailPanel}${mobileHidden ? ` ${styles.mobileHidden}` : ''}`

  // ========================================================================
  // 3-11. 읽기 모드 렌더링
  // ========================================================================
  if (mode === 'read') {
    return (
      <div className={panelClassName}>
        {/* 헤더: 배지 + 제목 + 버튼 */}
        <div className={styles.detailHeader}>
          <div className={styles.detailHeaderLeft}>
            <span className={`${styles.detailHeaderBadge} ${panelIndex === 0 ? styles.badge1 : styles.badge2}`}>
              📌 {panelIndex + 1}
            </span>
            <h4>{script.name || '이름 없음'}</h4>
          </div>
          <div className={styles.detailHeaderRight}>
            <button onClick={handleEnterEdit} title="수정">✏️</button>
            <button onClick={handleClose} title="닫기">✕</button>
          </div>
        </div>

        {/* 메타 정보 */}
        <div className={styles.detailMeta}>
          {script.category && (
            <span className={styles.detailMetaItem}>📁 {script.category}</span>
          )}
          <span className={styles.detailMetaItem}>📅 {formatDate(script.updatedAt)}</span>
          <span className={styles.detailMetaItem}>📝 {script.content.length}자</span>
        </div>

        {/* 내용 */}
        <div className={styles.detailContent}>
          {script.content || '내용 없음'}
        </div>

        {/* 액션 버튼 */}
        <div className={styles.detailActions}>
          {onOpenInEditor && (
            <button className={styles.detailEditBtn} onClick={() => onOpenInEditor(script)}>
              📝 에디터에서 열기
            </button>
          )}
          <button onClick={handleEnterEdit}>
            ✏️ 간편 수정
          </button>
          <button onClick={handleCopy}>
            📋 복사
          </button>
          <button
            className={styles.detailDeleteBtn}
            onClick={() => onDelete(script.id)}
          >
            🗑️ 삭제
          </button>
        </div>
      </div>
    )
  }

  // ========================================================================
  // 3-12. 수정 모드 렌더링
  // ========================================================================
  return (
    <div className={panelClassName}>
      {/* 헤더 */}
      <div className={styles.detailHeader}>
        <div className={styles.detailHeaderLeft}>
          <span className={`${styles.detailHeaderBadge} ${panelIndex === 0 ? styles.badge1 : styles.badge2}`}>
            📌 {panelIndex + 1}
          </span>
          <h4>수정 중</h4>
        </div>
        <div className={styles.detailHeaderRight}>
          <button onClick={handleClose} title="닫기">✕</button>
        </div>
      </div>

      {/* 수정 폼 */}
      <div className={styles.detailEditForm}>
        {/* 이름 입력 */}
        <div className={styles.formGroup}>
          <label>스크립트 이름</label>
          <input
            type="text"
            className={styles.nameInput}
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="스크립트 이름"
            disabled={isSaving}
          />
        </div>

        {/* 카테고리 입력 */}
        <div className={styles.formGroup}>
          <label>카테고리</label>
          <CategoryFilter
            value={editCategory}
            onChange={setEditCategory}
            categories={categories}
            disabled={isSaving}
          />
        </div>

        {/* 내용 편집 */}
        <div className={styles.formGroup}>
          <label>
            내용
            <CharacterCounter current={editContent.length} />
          </label>
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            placeholder="스크립트 내용을 입력하세요"
            disabled={isSaving}
          />
        </div>
      </div>

      {/* 수정 액션 버튼 */}
      <div className={styles.detailEditActions}>
        <button onClick={handleCancelEdit} disabled={isSaving}>
          취소
        </button>
        <button
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={isSaving || !editName.trim()}
        >
          {isSaving ? '저장 중...' : '💾 저장'}
        </button>
      </div>
    </div>
  )
}

export default ScriptDetailPanel
